/**
 * Entry-id helpers. The backend exposes ids as `ent_<bigint>` strings on the
 * wire. `formatEntryId` also accepts numeric ids at raw transaction/admin
 * boundaries, but canonical entry resources should pass/string-store wire ids.
 */
const PREFIX = "ent_";

export type EntryId = string;
export type EntryIdInput = EntryId | number;

export function formatEntryId(id: EntryIdInput): EntryId {
	if (typeof id === "string") return id.startsWith(PREFIX) ? id : `${PREFIX}${id}`;
	return `${PREFIX}${id}`;
}

export function parseEntryId(value: string): number {
	if (!value.startsWith(PREFIX)) {
		throw new Error(`Expected entry id "ent_<n>", got "${value}"`);
	}
	// The backend renders entry ids as `ent_<positive integer>` (a BigInt rendered
	// without a sign, decimal, or exponent). Require a digits-only suffix —
	// `Number("")` is 0 (finite), `Number("1.5")` is finite-but-not-integer,
	// `Number("0x10")` is finite, etc. None of those are valid v2 ids.
	const raw = value.slice(PREFIX.length);
	if (!/^\d+$/.test(raw)) {
		throw new Error(`Malformed entry id: "${value}"`);
	}
	const n = Number(raw);
	if (!Number.isSafeInteger(n) || n <= 0) {
		throw new Error(`Malformed entry id: "${value}"`);
	}
	return n;
}

/**
 * Convert a form duration (units, e.g. 0.1 / 0.2 / 0.5) into the
 * `unitsSeconds` integer the v2 create/edit endpoints require. The backend
 * re-derives `units` via `calculateUnits(seconds, isZurich)`, so sending the
 * straightforward `duration * 3600` round-trips correctly through the standard
 * 6-minute grid AND the Zurich 3-minute grid:
 *
 *  - 0.05 → 180s → Zurich: 0.05, non-Zurich: 0.1 (snap)
 *  - 0.1  → 360s → 0.1 in both grids
 *  - 0.2  → 720s → 0.2 in both grids
 */
export function durationToUnitsSeconds(duration: number): number {
	return Math.max(0, Math.round(duration * 3600));
}
